import api from "../../../utils/api";

// ============================================================================
// CRM Objects — frontend service for the entities the HubSpot suite brought in.
// Talks to the torre-de-control-api `crmObjects` module, one generic CRUD API
// over eleven collections:
//
//   writable : tasks, notes, tickets, emails, products, line_items, quotes
//   mirrors  : forms, form_submissions, campaigns, segments  (HubSpot-owned,
//              read-only on our side — the API rejects writes with 403)
//
//   • types  (authed): GET    /crm/objects/types
//   • list   (authed): GET    /crm/objects?type=tasks[&field=value][&search=]
//   • get    (authed): GET    /crm/objects/item?type=&id=
//   • create (authed): POST   /crm/objects?type=
//   • update (authed): PUT    /crm/objects?type=&id=
//   • delete (authed): DELETE /crm/objects?type=&id=
//
// Anything written here flows on to HubSpot through the same Firestore trigger
// the rest of the CRM uses — creating a task in the console creates it there too.
// ============================================================================

export type CrmObjectType =
  | "tasks"
  | "notes"
  | "tickets"
  | "emails"
  | "products"
  | "line_items"
  | "quotes"
  | "forms"
  | "form_submissions"
  | "campaigns"
  | "segments";

/** One entry of the server-side catalog — lets the UI render any list generically. */
export interface CrmObjectTypeDef {
  type: CrmObjectType;
  collection: string;
  label: string;
  icon: string;
  writable: boolean;
  fields: string[];
  required: string[];
  enums: Record<string, string[]>;
  defaults: Record<string, unknown>;
}

/** Every object shares the managed envelope; the rest is per-type. */
export interface CrmObject extends Record<string, unknown> {
  id: string;
  source?: string;
  hubspotId?: string | null;
  createdAt?: unknown;
  updatedAt?: unknown;
}

export interface CrmObjectPage<T extends CrmObject = CrmObject> {
  rows: T[];
  total: number;
  page: number;
  totalPages: number;
}

export async function fetchCrmObjectTypes(): Promise<CrmObjectTypeDef[]> {
  const res = await api.get("/crm/objects/types");
  return Array.isArray(res.data?.data) ? res.data.data : [];
}

/**
 * List one entity type. Any declared field can be passed in `filters` as an
 * equality filter, plus `search` for a free-text scan across string fields.
 */
export async function fetchCrmObjects<T extends CrmObject = CrmObject>(
  type: CrmObjectType,
  { page = 1, limit = 50, search, filters = {} }: {
    page?: number;
    limit?: number;
    search?: string;
    filters?: Record<string, string | undefined>;
  } = {},
): Promise<CrmObjectPage<T>> {
  const params: Record<string, string | number> = { type, page, limit };
  if (search) params.search = search;
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== "") params[k] = v;
  }
  const res = await api.get("/crm/objects", { params });
  const body = res.data ?? {};
  return {
    rows: Array.isArray(body.data) ? (body.data as T[]) : [],
    total: body.meta?.totalItems ?? 0,
    page: body.meta?.currentPage ?? page,
    totalPages: body.meta?.totalPages ?? 1,
  };
}

export async function fetchCrmObject<T extends CrmObject = CrmObject>(
  type: CrmObjectType,
  id: string,
): Promise<T> {
  const res = await api.get("/crm/objects/item", { params: { type, id } });
  return res.data?.data as T;
}

export async function createCrmObject<T extends CrmObject = CrmObject>(
  type: CrmObjectType,
  body: Record<string, unknown>,
): Promise<T> {
  const res = await api.post("/crm/objects", body, { params: { type } });
  return res.data?.data as T;
}

export async function updateCrmObject<T extends CrmObject = CrmObject>(
  type: CrmObjectType,
  id: string,
  body: Record<string, unknown>,
): Promise<T> {
  const res = await api.put("/crm/objects", body, { params: { type, id } });
  return res.data?.data as T;
}

export async function deleteCrmObject(type: CrmObjectType, id: string): Promise<void> {
  await api.delete("/crm/objects", { params: { type, id } });
}
